import { useEffect, useRef, useState } from 'react';
import {
    useCreateOrUpdateExpertMarketplaceMutation,
    useDeleteExpertMarketplaceMutation,
    useGetExpertMarketplacesMutation
} from '../../../store/user/userApi';
import { IExpertPartner } from '../../../interfaces/expert-partners/IExpertPartner';
import { IToast } from '../../../interfaces/components/IToast';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import Button from '../../../shared/button';
import Toast from '../../../shared/toast';
import DeleteModal from '../../../shared/delete-modal';
import ExpertPartnersTable from './components/ExpertPartnersTable';
import ManageExpertPartnerModal from './components/ManageExpertPartnerModal';
import { EXPERT_PARTNER_AGENTS } from './constants';

const getPartnerUniqueKey = (partner: IExpertPartner) => {
    return String(partner?._id || partner?.id || `${partner?.agent || ''}-${partner?.companyName || ''}-${partner?.logoUrl || ''}`);
};

const mergeWithoutDuplicates = (previousRows: IExpertPartner[], incomingRows: IExpertPartner[]) => {
    const existingKeys = new Set(previousRows.map((item) => getPartnerUniqueKey(item)));
    const mergedRows = [...previousRows];

    incomingRows.forEach((item) => {
        const itemKey = getPartnerUniqueKey(item);
        if (!existingKeys.has(itemKey)) {
            existingKeys.add(itemKey);
            mergedRows.push(item);
        }
    });

    return mergedRows;
};

const resolveResponseData = (response: any) => {
    const normalizedData = response?.data?.data || response?.data || response || {};
    return {
        elements: normalizedData?.elements || [],
        page: normalizedData?.page,
    };
};

const resolveHasMore = (page: any, incomingRows: IExpertPartner[], pageSize: number) => {
    if (typeof page?.hasNextPage === 'boolean') return page.hasNextPage;
    if (typeof page?.number === 'number' && typeof page?.totalPages === 'number') {
        if (page.number >= 1) {
            return page.number < page.totalPages;
        }
        return page.number + 1 < page.totalPages;
    }
    return incomingRows.length >= pageSize;
};

const getErrorMessage = (error: any) => {
    if (typeof error === 'string') return error;
    if (Array.isArray(error?.data?.message)) return error.data.message.join(', ');
    if (error?.data?.message) return error.data.message;
    if (error?.message) return error.message;
    return 'Something went wrong';
};

const ExpertPartners = () => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const requestNextPageRef = useRef(false);

    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [selectedAgent, setSelectedAgent] = useState<string>('');
    const [rows, setRows] = useState<IExpertPartner[]>([]);
    const [pageData, setPageData] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [showToast, setShowToast] = useState<IToast | null>(null);
    const [modalState, setModalState] = useState<{ open: boolean; item?: IExpertPartner }>({ open: false });
    const [deleteState, setDeleteState] = useState<{ open: boolean; item?: IExpertPartner }>({ open: false });

    const [getExpertMarketplaces] = useGetExpertMarketplacesMutation();
    const [createOrUpdateExpertMarketplace, { isLoading: saveLoading }] = useCreateOrUpdateExpertMarketplaceMutation();
    const [deleteExpertMarketplace] = useDeleteExpertMarketplaceMutation();

    useEffect(() => {
        const fetchExpertPartners = async () => {
            const isFirstPage = paginationState.pageNumber === 1;

            try {
                if (isFirstPage) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }

                const result = await getExpertMarketplaces({
                    ...paginationState,
                    data: selectedAgent ? { agent: selectedAgent } : {},
                }).unwrap();

                const { elements, page } = resolveResponseData(result);
                const incomingRows = elements as IExpertPartner[];

                setPageData(page);
                setRows((prevRows) => (isFirstPage ? incomingRows : mergeWithoutDuplicates(prevRows, incomingRows)));
                setHasMore(resolveHasMore(page, incomingRows, paginationState.pageSize || 10));
            } catch (error) {
                if (isFirstPage) {
                    setRows([]);
                    setPageData(undefined);
                }
                setHasMore(false);
                setShowToast({ type: 'error', message: getErrorMessage(error) });
            } finally {
                requestNextPageRef.current = false;
                setLoading(false);
                setLoadingMore(false);
            }
        };

        fetchExpertPartners();
    }, [
        getExpertMarketplaces,
        paginationState.pageNumber,
        paginationState.pageSize,
        selectedAgent,
        refreshKey,
    ]);

    const onScrollEnd = () => {
        if (!scrollContainerRef.current || !hasMore || loadingMore || loading || requestNextPageRef.current) return;

        const element = scrollContainerRef.current;
        if (element.scrollTop <= 0) return;

        const isNearBottom = element.scrollHeight - (element.scrollTop + element.clientHeight) <= 100;
        if (!isNearBottom) return;

        requestNextPageRef.current = true;
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: (prev.pageNumber || 1) + 1,
        }));
    };

    const onAgentFilterChange = (value: string) => {
        setSelectedAgent(value);
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
        setRefreshKey((prev) => prev + 1);
    };

    const onOpenCreateModal = () => {
        setModalState({ open: true, item: undefined });
    };

    const onOpenEditModal = (item: IExpertPartner) => {
        setModalState({ open: true, item });
    };

    const onCloseModal = () => {
        setModalState({ open: false, item: undefined });
    };

    const onOpenDeleteModal = (item: IExpertPartner) => {
        setDeleteState({ open: true, item });
    };

    const onCloseDeleteModal = () => {
        setDeleteState({ open: false, item: undefined });
    };

    const refreshList = () => {
        setRows([]);
        setPageData(undefined);
        setHasMore(true);
        requestNextPageRef.current = false;
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
        setRefreshKey((prev) => prev + 1);
    };

    const onSaveExpertPartner = async (payload: FormData) => {
        await createOrUpdateExpertMarketplace(payload).unwrap();
        setShowToast({
            type: 'success',
            message: modalState?.item ? 'Expert partner updated successfully' : 'Expert partner created successfully',
        });
        refreshList();
    };

    const onApproveDeletion = async () => {
        const selectedItem = deleteState.item;
        const itemId = selectedItem?._id;

        if (!itemId) {
            setShowToast({
                type: 'error',
                message: 'Missing _id for selected expert partner',
            });
            onCloseDeleteModal();
            return;
        }

        try {
            await deleteExpertMarketplace(itemId).unwrap();
            setRows((prevRows) => prevRows.filter((row) => row?._id !== itemId && row?.id !== itemId));
            setPageData((prevPage: any) => {
                if (!prevPage || typeof prevPage?.totalElements !== 'number') return prevPage;
                return {
                    ...prevPage,
                    totalElements: Math.max(0, prevPage.totalElements - 1),
                };
            });
            setShowToast({
                type: 'success',
                message: 'Expert partner deleted successfully',
            });
        } catch (error) {
            setShowToast({
                type: 'error',
                message: getErrorMessage(error),
            });
        } finally {
            onCloseDeleteModal();
        }
    };

    return (
        <div className='w-full'>
            <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
                    <div>
                        <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                            Expert Partners
                        </h1>
                        <p className='text-sm text-gray-600 dark:text-gray-300'>
                            Create, edit and manage expert marketplace partners.
                        </p>
                    </div>
                    <Button
                        label='Add Expert Partner'
                        className='btn-primary-rounded-less !min-w-[200px]'
                        onClickButton={onOpenCreateModal}
                    />
                </div>

                <div className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4'>
                    <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
                        <div className='flex flex-col md:flex-row md:items-end gap-3'>
                            <div className='w-full md:w-[260px]'>
                                <p className='mb-1 text-gray-600 font-medium text-sm dark:text-gray-300'>Filter By Agent</p>
                                <select
                                    value={selectedAgent}
                                    onChange={(e) => onAgentFilterChange(e.target.value)}
                                    className='select-primary dark:bg-black dark:text-white dark:border-gray-700'
                                >
                                    <option value=''>All Agents</option>
                                    {EXPERT_PARTNER_AGENTS.map((agent) => (
                                        <option key={agent.value} value={agent.value} className='dark:bg-black dark:text-white'>
                                            {agent.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {pageData?.totalElements != null && (
                            <p className='text-xs text-gray-500 dark:text-gray-400'>
                                {pageData.totalElements} total
                            </p>
                        )}
                    </div>
                </div>

                <ExpertPartnersTable
                    rows={rows}
                    loading={loading}
                    loadingMore={loadingMore}
                    hasMore={hasMore}
                    scrollContainerRef={scrollContainerRef}
                    onScrollEnd={onScrollEnd}
                    onEdit={onOpenEditModal}
                    onDelete={onOpenDeleteModal}
                />
            </div>

            {modalState.open && (
                <ManageExpertPartnerModal
                    openModal={modalState.open}
                    item={modalState.item}
                    loading={saveLoading}
                    handleCloseModal={onCloseModal}
                    onSubmit={onSaveExpertPartner}
                />
            )}

            {deleteState.open && (
                <DeleteModal
                    openDeleteModal={deleteState.open}
                    handleCloseDeleteModal={onCloseDeleteModal}
                    onApproveDeletion={onApproveDeletion}
                />
            )}

            {showToast?.message && (
                <Toast
                    type={showToast.type}
                    text={showToast.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            )}
        </div>
    );
};

export default ExpertPartners;
